import {
  canvas,
  drawQuad,
  drawSprite,
  drawSpriteRect,
  drawSpriteRotated,
  drawTriangles,
  lineToNdcQuad,
  rectToNdcQuad,
  whiteTexture,
} from "./gl.js";
import {
  clamp,
  lerp,
  approach,
  easeInOutCubic,
  fitSpriteSize,
  randomBetween,
} from "./utils.js";
import {
  computeContainedRect,
  getBoxById,
  getBoxCenterPixels,
  getBoxPixelSize,
  getBenchBaseSize,
  getPanelRectForInput,
  normalizedToPixel,
  pixelToNormalized,
  pointInRect,
} from "./layout.js";
import { getBenchCenters } from "./ui.js";
import {
  ACTIVE_CHARACTER_ID,
  ACTIVE_MONSTER_ID,
  appState,
  boardAnim,
  branchState,
  CHARACTER_BASE_OFFSET,
  CHARACTER_JUMP_HEIGHT,
  CHARACTER_SCALE,
  editor,
  ENTRY_JUMP_HEIGHT,
  FINISH_JUMP_COUNT,
  FINISH_JUMP_DURATION,
  FINISH_TO_GOAL_DURATION,
  FINISH_TO_GOAL_JUMP_HEIGHT,
  FINISH_TO_GOAL_STAGGER,
  gameState,
  ONE_MORE_DELAY,
  ONE_MORE_TOTAL,
  moveState,
  ROSTER_SCALE,
  ROSTER_SPAWN_MAX_DURATION,
  ROSTER_SPAWN_MIN_DURATION,
  ROSTER_SPAWN_STAGGER,
  ROSTER_WOBBLE_SPEED,
  rosterState,
  scoreConfig,
  selectionState,
  separationState,
  selectionArrowSprite,
  selectionAuraSprite,
  selectionRingSprite,
  shakeState,
  oneMoreOverlay,
  textures,
  WAITING_MOVE_SPEED,
  SELECTION_ARROW_BOB_HEIGHT,
  SELECTION_ARROW_BOB_SPEED,
  SELECTION_ARROW_OFFSET,
  SELECTION_ARROW_SCALE,
  yutButtonAnim,
} from "./state.js";
import { queueScoreEvent } from "./bags.js";
import { playSfx } from "./sfx.js";

let moveCompleteHandler = null;
const FULL_TEXCOORDS = new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]);

export function setMoveCompleteHandler(handler) {
  moveCompleteHandler = handler;
}

function requestDraw() {
  appState.requestDraw?.();
}

export function updateShake(dt) {
  if (!shakeState.active) {
    return;
  }
  shakeState.elapsed += dt;
  if (shakeState.elapsed >= shakeState.duration) {
    shakeState.active = false;
  }
}

export function getShakeOffset() {
  if (!shakeState.active) {
    return { x: 0, y: 0 };
  }
  const t = 1 - clamp(shakeState.elapsed / shakeState.duration, 0, 1);
  const strength = shakeState.strength * t;
  return {
    x: (Math.random() * 2 - 1) * strength,
    y: (Math.random() * 2 - 1) * strength,
  };
}

export function triggerShake(strength = 8, duration = 0.25, playThud = false) {
  shakeState.active = true;
  shakeState.elapsed = 0;
  shakeState.strength = strength;
  shakeState.duration = duration;
  if (playThud) {
    playSfx("land_thud");
  }
}

function getOffscreenTopY(panelRect) {
  return canvas.height + panelRect.height * 0.2;
}

export function isPiecesAnimating() {
  return rosterState.tokens.some(
    (piece) => piece.spawnActive || piece.entryActive || piece.knockActive || piece.finishActive
  );
}

export function renderPieces(panelRect, boardRect) {
  if (rosterState.spawnPending || boardAnim.active) {
    return;
  }
  const activeGroups = buildActiveGroups({ includeSpawning: true });
  const stackOffsets = computeStackOffsets(activeGroups, panelRect);
  const selectableIds = getSelectablePieceIds();
  const drawItems = [];

  for (const piece of rosterState.tokens) {
    if (!piece.ready || !piece.idle.texture) {
      continue;
    }
    const info = getPieceRenderInfo(piece, panelRect, boardRect, stackOffsets, selectableIds);
    if (!info) {
      continue;
    }
    drawItems.push(info);
  }

  drawItems.sort((a, b) => {
    const dy = b.sortY - a.sortY;
    if (Math.abs(dy) > 0.5) {
      return dy;
    }
    return a.order - b.order;
  });
  for (const item of drawItems) {
    if (item.rotation) {
      drawSpriteRotated(
        item.textureInfo,
        item.center,
        item.baseSize,
        item.scaleX,
        item.scaleY,
        item.rotation
      );
    } else {
      drawSprite(item.textureInfo, item.center, item.baseSize, item.scaleX, item.scaleY);
    }
    const piece = item.piece;
    if (piece && (piece.knockActive || piece.knockImpact)) {
      const fitted = fitSpriteSize(item.baseSize, item.textureInfo.width, item.textureInfo.height);
      const width = Math.abs(fitted.width * item.scaleX);
      const height = Math.abs(fitted.height * item.scaleY);
      const rect = {
        left: Math.round(item.center.x - width * 0.5),
        bottom: Math.round(item.center.y - height * 0.5),
        width: Math.round(width),
        height: Math.round(height),
      };
      const flicker = Math.sin(rosterState.time * 18);
      const color =
        flicker >= 0 ? [1, 1, 1, 0.45] : [1, 0.15, 0.15, 0.5];
      drawSpriteRect(item.textureInfo, rect, color);
    }
  }
}

export function renderSelectionArrows(panelRect, boardRect) {
  if (!selectionState.active) {
    return;
  }
  const groups = getSelectableGroups();
  if (!groups.length) {
    return;
  }
  const arrowReady = selectionArrowSprite.texture && selectionArrowSprite.width > 0;
  for (const group of groups) {
    const center = getGroupTopCenter(group, panelRect);
    const size = getGroupBaseSize(group, panelRect);
    const baseOffset = size * (0.55 + SELECTION_ARROW_OFFSET);
    const bob = Math.sin(rosterState.time * SELECTION_ARROW_BOB_SPEED) * (size * SELECTION_ARROW_BOB_HEIGHT);
    const arrowCenter = { x: center.x, y: center.y + baseOffset + bob };
    if (arrowReady) {
      const height = size * 0.35 * SELECTION_ARROW_SCALE;
      drawSprite(selectionArrowSprite, arrowCenter, height, 1, 1);
    } else {
      const arrowHeight = size * 0.25;
      const tip = { x: arrowCenter.x, y: arrowCenter.y };
      const left = { x: tip.x - arrowHeight * 0.6, y: tip.y + arrowHeight };
      const right = { x: tip.x + arrowHeight * 0.6, y: tip.y + arrowHeight };
      drawTrianglePixels(tip, left, right, [1, 0.25, 0.2, 0.9]);
    }
  }
}

export function renderBranchArrows(panelRect) {
  if (!branchState.active) {
    return;
  }
  const choices = getBranchChoiceInfos(panelRect);
  if (!choices.length) {
    return;
  }
  const arrowReady = selectionArrowSprite.texture && selectionArrowSprite.width > 0;
  const auraReady = selectionAuraSprite.texture && selectionAuraSprite.width > 0;
  const ringReady = selectionRingSprite.texture && selectionRingSprite.width > 0;
  const pulse = 0.5 + 0.5 * Math.sin(rosterState.time * 6);
  const groupCenter = branchState.group ? getGroupTopCenter(branchState.group, panelRect) : null;
  const curveStart = groupCenter
    ? { x: Math.round(groupCenter.x), y: Math.round(groupCenter.y) }
    : null;
  for (const choice of choices) {
    if (curveStart) {
      const end = { x: choice.center.x, y: choice.center.y };
      const mid = { x: (curveStart.x + end.x) * 0.5, y: (curveStart.y + end.y) * 0.5 };
      const dist = Math.hypot(end.x - curveStart.x, end.y - curveStart.y);
      const arc = Math.max(36, dist * 0.42);
      const control = { x: mid.x, y: mid.y + arc };
      const segments = Math.max(10, Math.round(dist / 28));
      let prev = getBezierPoint(curveStart, control, end, 0);
      for (let i = 1; i <= segments; i += 1) {
        const t = i / segments;
        const next = getBezierPoint(curveStart, control, end, t);
        drawLinePixels(
          { x: Math.round(prev.x), y: Math.round(prev.y) },
          { x: Math.round(next.x), y: Math.round(next.y) },
          Math.max(2, choice.rect.width * 0.045),
          [1, 1, 1, 0.35]
        );
        prev = next;
      }
      const triCount = 3;
      const speed = 0.35;
      for (let i = 0; i < triCount; i += 1) {
        const phase = (rosterState.time * speed + i / triCount) % 1;
        const t = 0.1 + phase * 0.8;
        const pos = getBezierPoint(curveStart, control, end, t);
        const tangent = getBezierTangent(curveStart, control, end, t);
        const angle = Math.atan2(tangent.y, tangent.x);
        drawDirectionalTrianglePixels(
          { x: Math.round(pos.x), y: Math.round(pos.y) },
          angle,
          Math.max(6, choice.rect.width * 0.12),
          [1, 0.95, 0.8, 0.9]
        );
      }
    }
    if (auraReady) {
      const auraSize = Math.round(choice.rect.width * (1.55 + pulse * 0.12));
      const auraRect = {
        left: Math.round(choice.center.x - auraSize * 0.5),
        bottom: Math.round(choice.center.y - auraSize * 0.5),
        width: auraSize,
        height: auraSize,
      };
      drawSpriteRect(selectionAuraSprite, auraRect, [1, 1, 1, 0.7 + pulse * 0.25]);
    }
    if (ringReady) {
      const ringSize = Math.round(choice.rect.width * (1.1 + pulse * 0.08));
      const ringRect = {
        left: Math.round(choice.center.x - ringSize * 0.5),
        bottom: Math.round(choice.center.y - ringSize * 0.5),
        width: ringSize,
        height: ringSize,
      };
      drawSpriteRect(selectionRingSprite, ringRect, [1, 1, 1, 0.8 + pulse * 0.15]);
    }
    if (choice.enemy) {
      const alpha = 0.3 + pulse * 0.5;
      drawSolidRectPixels(choice.rect, [1, 0.2, 0.2, alpha]);
    }
    const boxSize = choice.rect.width;
    const arrowSize = Math.max(12, Math.round(boxSize * 1.0));
    const bob = Math.sin(rosterState.time * SELECTION_ARROW_BOB_SPEED) * (arrowSize * SELECTION_ARROW_BOB_HEIGHT);
    const lift = choice.rect.height * 0.52;
    const arrowCenter = { x: choice.center.x, y: Math.round(choice.center.y + lift + bob) };
    if (arrowReady) {
      drawSprite(selectionArrowSprite, arrowCenter, arrowSize, 1, 1);
    } else {
      const half = arrowSize * 0.22;
      const tip = { x: arrowCenter.x, y: arrowCenter.y + half };
      const left = { x: arrowCenter.x - half, y: arrowCenter.y - half };
      const right = { x: arrowCenter.x + half, y: arrowCenter.y - half };
      drawTrianglePixels(tip, left, right, [1, 0.25, 0.2, 0.9]);
    }
  }
}

export function pickSelectableGroupAtPoint(point, panelRect) {
  const groups = getSelectableGroups();
  let bestGroup = null;
  let bestDist = Infinity;
  for (const group of groups) {
    const center = getGroupTopCenter(group, panelRect);
    const size = getGroupBaseSize(group, panelRect);
    const rect = {
      left: center.x - size * 0.5,
      bottom: center.y - size * 0.5,
      width: size,
      height: size,
    };
    if (!pointInRect(point, rect)) {
      continue;
    }
    const dx = point.x - center.x;
    const dy = point.y - center.y;
    const dist = dx * dx + dy * dy;
    if (dist < bestDist) {
      bestDist = dist;
      bestGroup = group;
    }
  }
  return bestGroup;
}

export function clearBranchState() {
  branchState.active = false;
  branchState.team = null;
  branchState.steps = 0;
  branchState.group = null;
  branchState.fromBoxId = null;
  branchState.candidates = [];
  branchState.choices = [];
}

export function pickBranchChoiceAtPoint(point, panelRect) {
  if (!branchState.active) {
    return null;
  }
  const choices = getBranchChoiceInfos(panelRect);
  for (const choice of choices) {
    if (pointInRect(point, choice.rect)) {
      return choice.nextId;
    }
  }
  return null;
}

function drawTrianglePixels(p0, p1, p2, color) {
  const positions = new Float32Array([
    (p0.x / canvas.width) * 2 - 1,
    (p0.y / canvas.height) * 2 - 1,
    (p1.x / canvas.width) * 2 - 1,
    (p1.y / canvas.height) * 2 - 1,
    (p2.x / canvas.width) * 2 - 1,
    (p2.y / canvas.height) * 2 - 1,
  ]);
  const texcoords = new Float32Array([0, 0, 1, 0, 0, 1]);
  drawTriangles(positions, texcoords, color);
}

function drawSolidRectPixels(rect, color) {
  const quad = rectToNdcQuad(rect);
  drawQuad(quad, FULL_TEXCOORDS, whiteTexture, color);
}

function drawLinePixels(start, end, thickness, color) {
  const quad = lineToNdcQuad(start, end, thickness, canvas.width, canvas.height);
  if (!quad) {
    return;
  }
  drawQuad(quad, FULL_TEXCOORDS, whiteTexture, color);
}

function getBezierPoint(p0, p1, p2, t) {
  const u = 1 - t;
  const uu = u * u;
  const tt = t * t;
  return {
    x: uu * p0.x + 2 * u * t * p1.x + tt * p2.x,
    y: uu * p0.y + 2 * u * t * p1.y + tt * p2.y,
  };
}

function getBezierTangent(p0, p1, p2, t) {
  const u = 1 - t;
  return {
    x: 2 * u * (p1.x - p0.x) + 2 * t * (p2.x - p1.x),
    y: 2 * u * (p1.y - p0.y) + 2 * t * (p2.y - p1.y),
  };
}

function drawDirectionalTrianglePixels(center, angle, size, color) {
  const dirX = Math.cos(angle);
  const dirY = Math.sin(angle);
  const perpX = -dirY;
  const perpY = dirX;
  const tip = {
    x: center.x + dirX * size,
    y: center.y + dirY * size,
  };
  const baseCenter = {
    x: center.x - dirX * size * 0.6,
    y: center.y - dirY * size * 0.6,
  };
  const half = size * 0.5;
  const left = {
    x: baseCenter.x + perpX * half,
    y: baseCenter.y + perpY * half,
  };
  const right = {
    x: baseCenter.x - perpX * half,
    y: baseCenter.y - perpY * half,
  };
  drawTrianglePixels(tip, left, right, color);
}

function getPieceRenderInfo(piece, panelRect, boardRect, stackOffsets, selectableIds) {
  const baseSize = getPieceBaseSize(piece, panelRect);
  if (baseSize <= 0) {
    return null;
  }
  const textureInfo = getPieceTexture(piece, selectableIds);
  let center = getPieceCenter(piece, panelRect, boardRect);
  let scaleX = piece.team === "player" ? 1 : -1;
  let scaleY = 1;
  let rotation = 0;

  const wobbleT = rosterState.time + piece.phase;
  const wobble = Math.sin(wobbleT * ROSTER_WOBBLE_SPEED) * 0.035;
  const squash = Math.sin(wobbleT * ROSTER_WOBBLE_SPEED + 1.4) * 0.022;

  if (piece.spawnActive) {
    const t = clamp(piece.spawnProgress / piece.spawnDuration, 0, 1);
    const arc = Math.sin(Math.PI * t);
    center.y += arc * baseSize * 0.9;
    const stretch = arc * 0.18;
    scaleY = 1 + stretch;
    scaleX = (piece.team === "player" ? 1 : -1) * (1 - stretch * 0.6);
  } else if (piece.knockActive) {
    const t = clamp(piece.knockProgress / piece.knockDuration, 0, 1);
    scaleX = piece.team === "player" ? 1 : -1;
    scaleY = 1;
    rotation = piece.knockSpin * t;
    const arc = Math.sin(Math.PI * t);
    center.y += arc * baseSize * 0.8;
  } else if (piece.entryActive) {
    const t = clamp(piece.entryProgress / piece.entryDuration, 0, 1);
    const arc = Math.sin(Math.PI * t);
    center.y += arc * baseSize * ENTRY_JUMP_HEIGHT;
    const stretch = arc * 0.18;
    scaleY = 1 + stretch;
    scaleX = (piece.team === "player" ? 1 : -1) * (1 - stretch * 0.6);
  } else if (piece.finishActive) {
    if (piece.finishPhase === "celebrate") {
      const t = clamp(piece.finishProgress, 0, 1);
      const jump = Math.sin(Math.PI * t);
      center.y += Math.max(0, jump) * baseSize * 0.6;
    } else if (piece.finishPhase === "toGoal") {
      const t = clamp(piece.finishProgress, 0, 1);
      const arc = Math.sin(Math.PI * t);
      center.y += arc * baseSize * FINISH_TO_GOAL_JUMP_HEIGHT;
    }
  } else if (moveState.active && moveState.pieces.includes(piece)) {
    const t = clamp(moveState.progress, 0, 1);
    const eased = easeInOutCubic(t);
    const fromBox = getBoxById(moveState.from);
    const toBox = getBoxById(moveState.to);
    if (fromBox && toBox) {
      const fromPx = getBoxCenterPixels(fromBox, panelRect);
      const toPx = getBoxCenterPixels(toBox, panelRect);
      center = {
        x: lerp(fromPx.x, toPx.x, eased),
        y: lerp(fromPx.y, toPx.y, eased),
      };
      const arc = Math.sin(Math.PI * t);
      center.y += arc * baseSize * CHARACTER_JUMP_HEIGHT;
      const stretch = arc * 0.18;
      scaleY = 1 + stretch;
      scaleX = (piece.team === "player" ? 1 : -1) * (1 - stretch * 0.6);
    }
  } else {
    scaleX = (piece.team === "player" ? 1 : -1) * (1 + wobble);
    scaleY = 1 - wobble * 0.8 + squash;
  }

  if (stackOffsets.has(piece.id)) {
    const offset = stackOffsets.get(piece.id);
    if (offset) {
      center.x += offset.x;
      center.y += offset.y;
    }
  }

  if (shouldSeparateActive()) {
    if (piece.status === "active" && piece.boxId === editor.startBoxId) {
      if (piece.team === "player") {
        center.x += separationState.activePlayerX;
      } else {
        center.x += separationState.activeMonsterX;
        center.y += separationState.activeMonsterY;
      }
    }
  }

  const fitted = fitSpriteSize(baseSize, textureInfo.width, textureInfo.height);
  const sortCenterY = center.y + baseSize * CHARACTER_BASE_OFFSET;
  center.y += (scaleY - 1) * fitted.height * 0.5;
  center.y += baseSize * CHARACTER_BASE_OFFSET;

  return {
    piece,
    textureInfo,
    center,
    baseSize,
    scaleX,
    scaleY,
    rotation,
    sortY: sortCenterY - fitted.height * 0.5,
    order: piece.order || 0,
  };
}

function getPieceTexture(piece, selectableIds) {
  if (
    (piece.spawnActive ||
      piece.entryActive ||
      piece.knockActive ||
      piece.finishActive ||
      moveState.active) &&
    piece.jump &&
    piece.jump.texture
  ) {
    return piece.jump;
  }
  if (
    selectableIds &&
    selectableIds.has(piece.id) &&
    piece.jump &&
    piece.jump.texture &&
    piece.idle &&
    piece.idle.texture
  ) {
    const phase = rosterState.time % 1;
    return phase < 0.5 ? piece.idle : piece.jump;
  }
  return piece.idle;
}

function getPieceBaseSize(piece, panelRect) {
  if (piece.status === "active" && piece.boxId != null) {
    const box = getBoxById(piece.boxId);
    if (box) {
      return getBoxPixelSize(box, panelRect) * CHARACTER_SCALE;
    }
  }
  return getBenchBaseSize(panelRect) * ROSTER_SCALE;
}

function getPieceCenter(piece, panelRect, boardRect, options = null) {
  const ignoreSpawn = options?.ignoreSpawn === true;
  if (!ignoreSpawn && piece.spawnActive && piece.spawnFrom && piece.spawnTo) {
    const t = clamp(piece.spawnProgress / piece.spawnDuration, 0, 1);
    const from = normalizedToPixel(piece.spawnFrom, panelRect);
    const to = normalizedToPixel(piece.spawnTo, panelRect);
    return { x: lerp(from.x, to.x, t), y: lerp(from.y, to.y, t) };
  }
  if (piece.knockActive && piece.knockFrom && piece.knockTo) {
    const t = clamp(piece.knockProgress / piece.knockDuration, 0, 1);
    const from = normalizedToPixel(piece.knockFrom, panelRect);
    const to = normalizedToPixel(piece.knockTo, panelRect);
    return { x: lerp(from.x, to.x, t), y: lerp(from.y, to.y, t) };
  }
  if (piece.entryActive && piece.entryFrom && piece.entryTo) {
    const t = clamp(piece.entryProgress / piece.entryDuration, 0, 1);
    const from = normalizedToPixel(piece.entryFrom, panelRect);
    const to = normalizedToPixel(piece.entryTo, panelRect);
    return { x: lerp(from.x, to.x, t), y: lerp(from.y, to.y, t) };
  }
  if (piece.finishActive) {
    if (piece.finishPhase === "celebrate" && piece.finishFrom) {
      return normalizedToPixel(piece.finishFrom, panelRect);
    }
    if (piece.finishPhase === "toGoal" && piece.finishFrom && piece.finishTo) {
      const t = clamp(piece.finishProgress, 0, 1);
      const from = normalizedToPixel(piece.finishFrom, panelRect);
      const to = normalizedToPixel(piece.finishTo, panelRect);
      return { x: lerp(from.x, to.x, t), y: lerp(from.y, to.y, t) };
    }
  }
  if (piece.status === "waiting") {
    const target = getWaitingTargetNormalized(panelRect, piece);
    const waitPos = piece.waitingPos || target;
    return normalizedToPixel(waitPos, panelRect);
  }
  if (piece.status === "finished") {
    const goalCenter = getGoalCenter(piece, panelRect, boardRect);
    return goalCenter;
  }
  if (piece.boxId != null) {
    const box = getBoxById(piece.boxId);
    if (box) {
      return getBoxCenterPixels(box, panelRect);
    }
  }
  return { x: panelRect.left, y: panelRect.bottom };
}

function getGoalCenter(piece, panelRect, boardRect) {
  const offset = piece.goalOffset || { x: 0.5, y: 0.5 };
  return getGoalCenterFromOffset(piece.team, offset, panelRect, boardRect);
}

function getGoalArea(panelRect, boardRect) {
  const rightEdge = boardRect.left - panelRect.width * 0.02;
  const leftEdge = panelRect.left + panelRect.width * 0.03;
  const width = Math.max(panelRect.width * 0.12, rightEdge - leftEdge);
  const height = panelRect.height * 0.26;
  const bottom = panelRect.bottom + panelRect.height * 0.06;
  return {
    left: leftEdge,
    bottom,
    width,
    height,
  };
}

function getGoalCenterFromOffset(team, offset, panelRect, boardRect) {
  const goalArea = getGoalArea(panelRect, boardRect);
  const teamHeight = goalArea.height * 0.5;
  const teamBottom = team === "player" ? goalArea.bottom + teamHeight : goalArea.bottom;
  const baseSize = getBenchBaseSize(panelRect) * ROSTER_SCALE;
  const margin = baseSize * 0.55;
  const usableWidth = Math.max(1, goalArea.width - margin * 2);
  const usableHeight = Math.max(1, teamHeight - margin * 2);
  const clampedOffset = {
    x: clamp(offset.x, 0, 1),
    y: clamp(offset.y, 0, 1),
  };
  return {
    x: goalArea.left + margin + clampedOffset.x * usableWidth,
    y: teamBottom + margin + clampedOffset.y * usableHeight,
  };
}

function getWaitingTargetNormalized(panelRect, piece) {
  const benchCenters = getBenchCenters(panelRect);
  const baseCenter = piece.team === "player" ? benchCenters.player : benchCenters.monster;
  const baseSize = getBenchBaseSize(panelRect);
  const target = {
    x: baseCenter.x + piece.offset.x * baseSize + piece.separationX,
    y: baseCenter.y + piece.offset.y * baseSize,
  };
  return pixelToNormalized(target, panelRect);
}

function buildActiveGroups(options = null) {
  const includeSpawning = options?.includeSpawning === true;
  const groups = new Map();
  for (const piece of rosterState.tokens) {
    if (
      piece.status !== "active" ||
      piece.boxId == null ||
      (!includeSpawning && piece.spawnActive) ||
      piece.entryActive ||
      piece.knockActive ||
      piece.finishActive
    ) {
      continue;
    }
    const key = `${piece.team}:${piece.boxId}`;
    if (!groups.has(key)) {
      groups.set(key, { team: piece.team, boxId: piece.boxId, pieces: [] });
    }
    groups.get(key).pieces.push(piece);
  }
  return Array.from(groups.values());
}

function computeStackOffsets(groups, panelRect) {
  const offsets = new Map();
  for (const group of groups) {
    const box = getBoxById(group.boxId);
    if (!box) {
      continue;
    }
    const baseSize = getBoxPixelSize(box, panelRect) * CHARACTER_SCALE;
    const pieces = group.pieces.slice().sort((a, b) => a.id.localeCompare(b.id));
    const count = pieces.length;
    const spacing = baseSize * 0.35;
    for (let i = 0; i < pieces.length; i += 1) {
      const offsetX = (i - (count - 1) / 2) * spacing;
      offsets.set(pieces[i].id, { x: offsetX, y: 0 });
    }
  }
  return offsets;
}

function getSelectableGroups() {
  if (!selectionState.active) {
    return [];
  }
  const groups = Array.isArray(selectionState.groups) ? selectionState.groups : [];
  return groups.filter(
    (group) =>
      group.team === "player" &&
      group.pieces.some(
        (piece) =>
          !piece.spawnActive &&
          !piece.entryActive &&
          !piece.knockActive &&
          !piece.finishActive
      )
  );
}

function getSelectablePieceIds() {
  if (!selectionState.active) {
    return null;
  }
  const groups = getSelectableGroups();
  if (!groups.length) {
    return null;
  }
  const ids = new Set();
  for (const group of groups) {
    for (const piece of group.pieces) {
      ids.add(piece.id);
    }
  }
  return ids;
}

function getBranchChoiceInfos(panelRect) {
  if (!branchState.active || !branchState.group) {
    return [];
  }
  const candidates = Array.isArray(branchState.candidates) ? branchState.candidates : [];
  const choices = [];
  const seen = new Set();
  const provided =
    Array.isArray(branchState.choices) && branchState.choices.length
      ? branchState.choices
      : candidates.map((nextId) => ({ nextId, landingId: nextId, enemy: false }));
  for (const entry of provided) {
    const nextId = Number(entry.nextId);
    const landingId = Number.isFinite(entry.landingId) ? entry.landingId : nextId;
    if (!Number.isFinite(landingId)) {
      continue;
    }
    if (seen.has(landingId)) {
      continue;
    }
    const landingBox = getBoxById(landingId);
    if (!landingBox) {
      continue;
    }
    seen.add(landingId);
    const boxSize = getBoxPixelSize(landingBox, panelRect);
    const centerRaw = getBoxCenterPixels(landingBox, panelRect);
    const center = { x: Math.round(centerRaw.x), y: Math.round(centerRaw.y) };
    const rect = {
      left: Math.round(center.x - boxSize * 0.5),
      bottom: Math.round(center.y - boxSize * 0.5),
      width: Math.round(boxSize),
      height: Math.round(boxSize),
    };
    choices.push({
      nextId,
      landingId,
      center,
      rect,
      enemy: !!entry.enemy,
    });
  }
  return choices;
}

function getGroupBaseSize(group, panelRect) {
  const box = getBoxById(group.boxId);
  if (!box) {
    return getBenchBaseSize(panelRect) * ROSTER_SCALE;
  }
  return getBoxPixelSize(box, panelRect) * CHARACTER_SCALE;
}

function getGroupTopCenter(group, panelRect) {
  const box = getBoxById(group.boxId);
  const center = box ? getBoxCenterPixels(box, panelRect) : { x: panelRect.left, y: panelRect.bottom };
  const baseSize = getGroupBaseSize(group, panelRect);
  let offsetX = 0;
  let offsetY = baseSize * 0.1;
  if (shouldSeparateActive() && group.boxId === editor.startBoxId) {
    if (group.team === "player") {
      offsetX += separationState.activePlayerX;
    } else {
      offsetX += separationState.activeMonsterX;
      offsetY += separationState.activeMonsterY;
    }
  }
  return { x: center.x + offsetX, y: center.y + offsetY };
}

function shouldSeparateActive() {
  if (editor.startBoxId == null || moveState.active) {
    return false;
  }
  const groups = buildActiveGroups({ includeSpawning: true });
  const playerGroup = groups.find(
    (group) => group.team === "player" && group.boxId === editor.startBoxId
  );
  const monsterGroup = groups.find(
    (group) => group.team === "monster" && group.boxId === editor.startBoxId
  );
  return !!(playerGroup && monsterGroup);
}
export function updateCharacterSeparation(dt, panelRect) {
  updateActiveSeparationTargets(panelRect);
  updateRosterSeparationTargets(panelRect);
  const speed = 12;
  let animating = false;

  separationState.activePlayerX = approach(
    separationState.activePlayerX,
    separationState.targetPlayerX,
    dt,
    speed
  );
  separationState.activeMonsterX = approach(
    separationState.activeMonsterX,
    separationState.targetMonsterX,
    dt,
    speed
  );
  separationState.activeMonsterY = approach(
    separationState.activeMonsterY,
    separationState.targetMonsterY,
    dt,
    speed
  );

  if (Math.abs(separationState.activePlayerX - separationState.targetPlayerX) > 0.5) {
    animating = true;
  }
  if (Math.abs(separationState.activeMonsterX - separationState.targetMonsterX) > 0.5) {
    animating = true;
  }
  if (Math.abs(separationState.activeMonsterY - separationState.targetMonsterY) > 0.5) {
    animating = true;
  }

  for (const token of rosterState.tokens) {
    token.separationX = approach(token.separationX, token.separationTargetX, dt, speed);
    if (Math.abs(token.separationX - token.separationTargetX) > 0.5) {
      animating = true;
    }
  }

  separationState.animating = animating;
}

function updateActiveSeparationTargets(panelRect) {
  separationState.targetPlayerX = 0;
  separationState.targetMonsterX = 0;
  separationState.targetMonsterY = 0;
  if (editor.startBoxId == null) {
    return;
  }
  if (moveState.active) {
    return;
  }
  const groups = buildActiveGroups({ includeSpawning: true });
  const playerGroup = groups.find(
    (group) => group.team === "player" && group.boxId === editor.startBoxId
  );
  const monsterGroup = groups.find(
    (group) => group.team === "monster" && group.boxId === editor.startBoxId
  );
  if (!playerGroup || !monsterGroup) {
    return;
  }
  const startBox = getBoxById(editor.startBoxId);
  if (!startBox) {
    return;
  }
  const baseSize = getBoxPixelSize(startBox, panelRect) * CHARACTER_SCALE;
  const shift = baseSize * 0.3;
  separationState.targetPlayerX = -shift;
  separationState.targetMonsterX = shift;
  separationState.targetMonsterY = -baseSize * 0.18;
}

function updateRosterSeparationTargets(panelRect) {
  const baseSize = getBenchBaseSize(panelRect) * ROSTER_SCALE;
  const benchCenters = getBenchCenters(panelRect);
  const rosterTokens = rosterState.tokens.filter(
    (token) => token.ready && token.idle.texture && token.status === "waiting"
  );
  const signature = rosterTokens
    .map((token) => token.id)
    .sort()
    .join("|");
  const signatureChanged = signature !== rosterState.waitingSignature;
  if (signatureChanged) {
    rosterState.waitingSignature = signature;
  }

  for (const token of rosterState.tokens) {
    if (token.status !== "waiting") {
      token.separationTargetX = 0;
      continue;
    }
    if (signatureChanged || typeof token.separationTargetX !== "number") {
      token.separationTargetX = token.separationX || 0;
    }
  }

  if (rosterTokens.length < 2) {
    return;
  }

  for (let pass = 0; pass < 2; pass += 1) {
    for (let i = 0; i < rosterTokens.length; i += 1) {
      for (let j = i + 1; j < rosterTokens.length; j += 1) {
        const a = rosterTokens[i];
        const b = rosterTokens[j];
        const baseCenterA = a.team === "player" ? benchCenters.player : benchCenters.monster;
        const baseCenterB = b.team === "player" ? benchCenters.player : benchCenters.monster;
        const ax = baseCenterA.x + a.offset.x * baseSize + a.separationTargetX;
        const bx = baseCenterB.x + b.offset.x * baseSize + b.separationTargetX;
        const dx = bx - ax;
        const dist = Math.abs(dx);
        const teamScale = a.team === b.team ? 0.48 : 0.75;
        const minDist = baseSize * teamScale;
        if (dist >= minDist) {
          continue;
        }
        const dir = dist > 0.001 ? Math.sign(dx) : i % 2 === 0 ? 1 : -1;
        const shift = (minDist - dist) * 0.5;
        a.separationTargetX -= dir * shift;
        b.separationTargetX += dir * shift;
      }
    }
  }
}

function snapSeparationTargets(panelRect) {
  updateActiveSeparationTargets(panelRect);
  updateRosterSeparationTargets(panelRect);
  separationState.activePlayerX = separationState.targetPlayerX;
  separationState.activeMonsterX = separationState.targetMonsterX;
  separationState.activeMonsterY = separationState.targetMonsterY;
  for (const token of rosterState.tokens) {
    token.separationX = token.separationTargetX;
  }
  separationState.animating = false;
}

export function updatePieces(dt, panelRect) {
  const boardRect = getLogicBoardRect(panelRect);
  rosterState.time += dt;
  for (const piece of rosterState.tokens) {
    if (piece.spawnActive) {
      if (!piece.ready || !piece.idle.texture) {
        continue;
      }
      if (piece.spawnFrom) {
        const target = getPieceCenter(piece, panelRect, boardRect, { ignoreSpawn: true });
        piece.spawnTo = pixelToNormalized(target, panelRect);
      }
      let remaining = dt;
      if (piece.spawnDelayRemaining > 0) {
        const consumed = Math.min(remaining, piece.spawnDelayRemaining);
        piece.spawnDelayRemaining -= consumed;
        remaining -= consumed;
        if (remaining <= 0) {
          continue;
        }
      }
      const duration = piece.spawnDuration || ROSTER_SPAWN_MIN_DURATION;
      piece.spawnProgress += remaining / duration;
      if (piece.spawnProgress >= 1) {
        piece.spawnProgress = 1;
        piece.spawnActive = false;
        if (piece.status === "waiting") {
          piece.waitingPos = piece.spawnTo ? { ...piece.spawnTo } : null;
        } else {
          piece.waitingPos = null;
        }
      }
      continue;
    }

    if (piece.entryActive) {
      piece.entryProgress += dt / piece.entryDuration;
      if (piece.entryProgress >= 1) {
        piece.entryProgress = 1;
        piece.entryActive = false;
        piece.status = "active";
        if (!moveState.active || !moveState.pieces.includes(piece)) {
          piece.boxId = editor.startBoxId;
        }
        piece.entryFrom = null;
        piece.entryTo = null;
        piece.waitingPos = null;
      }
      continue;
    }

    if (piece.knockActive) {
      piece.knockProgress += dt / piece.knockDuration;
      const t = clamp(piece.knockProgress, 0, 1);
      if (!piece.knockImpact && t >= 0.75) {
        piece.knockImpact = true;
        triggerShake(10, 0.25, true);
      }
      if (piece.knockProgress >= 1) {
        piece.knockProgress = 1;
        piece.knockActive = false;
        piece.status = "waiting";
        piece.boxId = null;
        piece.hasLeftStart = false;
        piece.goalIndex = null;
        piece.goalOffset = null;
        piece.waitingPos = piece.knockTo ? { ...piece.knockTo } : null;
        piece.knockFrom = null;
        piece.knockTo = null;
        piece.knockImpact = false;
      }
      continue;
    }

    if (piece.finishActive) {
      if (piece.finishPhase === "celebrate") {
        piece.finishProgress += dt / FINISH_JUMP_DURATION;
        if (piece.finishProgress >= 1) {
          piece.finishProgress = 0;
          piece.finishJumpCount += 1;
          if (piece.finishJumpCount >= FINISH_JUMP_COUNT) {
            piece.finishPhase = "toGoal";
            piece.finishProgress = 0;
          }
        }
      } else if (piece.finishPhase === "toGoal") {
        if (piece.finishDelayRemaining > 0) {
          piece.finishDelayRemaining = Math.max(0, piece.finishDelayRemaining - dt);
          continue;
        }
        piece.finishProgress += dt / FINISH_TO_GOAL_DURATION;
        if (piece.finishProgress >= 1) {
          piece.finishProgress = 1;
          piece.finishActive = false;
          piece.status = "finished";
          piece.boxId = null;
          const finishOrigin = piece.finishTo || piece.finishFrom;
          piece.finishFrom = piece.finishTo;
          piece.finishTo = null;
          piece.finishDelayRemaining = 0;
          playSfx("goal_piece");
        }
      }
    }
  }

  if (!panelRect) {
    return;
  }
  for (const piece of rosterState.tokens) {
    if (
      piece.status !== "waiting" ||
      piece.spawnActive ||
      piece.entryActive ||
      piece.knockActive ||
      piece.finishActive
    ) {
      continue;
    }
    const target = getWaitingTargetNormalized(panelRect, piece);
    if (!piece.waitingPos) {
      piece.waitingPos = { ...target };
      continue;
    }
    piece.waitingPos.x = approach(piece.waitingPos.x, target.x, dt, WAITING_MOVE_SPEED);
    piece.waitingPos.y = approach(piece.waitingPos.y, target.y, dt, WAITING_MOVE_SPEED);
  }
}

export function updateMoveState(dt, panelRect) {
  if (!moveState.active) {
    return;
  }
  if (moveState.pieces.some((piece) => piece.entryActive)) {
    return;
  }
  moveState.progress += dt / moveState.stepDuration;
  while (moveState.active && moveState.progress >= 1) {
    moveState.progress -= 1;
    const completedBoxId = moveState.to;
    for (const piece of moveState.pieces) {
      piece.boxId = completedBoxId;
      if (moveState.from === editor.startBoxId) {
        piece.hasLeftStart = true;
      }
    }
    playSfx("move_piece");

    moveState.stepIndex += 1;
    if (moveState.stepIndex >= moveState.path.length) {
      finishMoveState();
      break;
    }

    moveState.from = completedBoxId;
    moveState.to = moveState.path[moveState.stepIndex];
  }
}

function finishMoveState() {
  const team = moveState.team;
  moveState.active = false;
  moveState.team = null;
  moveState.pieces = [];
  moveState.path = [];
  moveState.stepIndex = 0;
  moveState.progress = 0;
  moveState.from = null;
  moveState.to = null;
  if (moveCompleteHandler) {
    moveCompleteHandler(team);
  }
}
export function startMoveGroupFromPath(group, path, panelRect) {
  if (!group || moveState.active || !path || !path.length) {
    return false;
  }
  moveState.active = true;
  moveState.team = group.team;
  moveState.pieces = group.pieces.slice();
  moveState.path = path.slice();
  moveState.stepIndex = 0;
  moveState.progress = 0;
  moveState.from = group.boxId;
  moveState.to = path[0];
  requestDraw();
  return true;
}

export function applyServerMoveEffects(effects, panelRect) {
  if (!effects) {
    return;
  }
  const landingId = Number.isFinite(effects.landingId) ? effects.landingId : null;
  if (Array.isArray(effects.knockIds)) {
    for (const id of effects.knockIds) {
      const token = rosterState.tokens.find((piece) => piece.id === id);
      if (!token) {
        continue;
      }
      if (landingId != null) {
        token.status = "active";
        token.boxId = landingId;
      }
      startKnockForPiece(token, panelRect);
    }
  }
  if (Array.isArray(effects.finish)) {
    const pieces = [];
    for (const entry of effects.finish) {
      const token = rosterState.tokens.find((piece) => piece.id === entry.id);
      if (token) {
        if (Number.isFinite(entry.goalIndex)) {
          token.goalIndex = entry.goalIndex;
        }
        pieces.push(token);
      }
    }
    if (pieces.length) {
      startFinishForGroup(pieces, panelRect, { fromServer: true });
    }
  }
}

function spawnWaitingPiece(team, panelRect) {
  if (editor.startBoxId == null) {
    return null;
  }
  const waiting = rosterState.tokens.filter(
    (token) =>
      token.team === team &&
      token.status === "waiting" &&
      !token.spawnActive &&
      !token.entryActive &&
      !token.knockActive &&
      !token.finishActive
  );
  if (!waiting.length) {
    return null;
  }
  const piece = waiting[Math.floor(Math.random() * waiting.length)];
  return startEntryForPiece(piece, panelRect) ? piece : null;
}

export function startEntryForPieceById(pieceId, panelRect) {
  if (!pieceId) {
    return false;
  }
  const piece = rosterState.tokens.find((token) => token.id === pieceId);
  if (!piece) {
    return false;
  }
  const ok = startEntryForPiece(piece, panelRect);
  if (ok) {
    requestDraw();
  }
  return ok;
}

function startEntryForPiece(piece, panelRect) {
  const startBox = editor.startBoxId != null ? getBoxById(editor.startBoxId) : null;
  if (!startBox) {
    return false;
  }
  const startCenter = getBoxCenterPixels(startBox, panelRect);
  const benchFrom = piece.waitingPos
    ? normalizedToPixel(piece.waitingPos, panelRect)
    : normalizedToPixel(getWaitingTargetNormalized(panelRect, piece), panelRect);
  const from = {
    x: benchFrom.x,
    y: benchFrom.y,
  };
  piece.spawnActive = false;
  piece.entryActive = true;
  piece.entryProgress = 0;
  piece.entryDuration = randomBetween(ROSTER_SPAWN_MIN_DURATION, ROSTER_SPAWN_MAX_DURATION);
  piece.entryFrom = pixelToNormalized(from, panelRect);
  piece.entryTo = pixelToNormalized(startCenter, panelRect);
  piece.status = "active";
  piece.boxId = editor.startBoxId;
  piece.hasLeftStart = false;
  piece.knockActive = false;
  piece.finishActive = false;
  piece.goalIndex = null;
  return true;
}

function startKnockForPiece(piece, panelRect) {
  const benchCenters = getBenchCenters(panelRect);
  const baseSize = getBenchBaseSize(panelRect);
  const baseCenter = piece.team === "player" ? benchCenters.player : benchCenters.monster;
  const target = {
    x: baseCenter.x + piece.offset.x * baseSize,
    y: baseCenter.y + piece.offset.y * baseSize,
  };
  const fromBox = piece.boxId != null ? getBoxById(piece.boxId) : null;
  const from = fromBox ? getBoxCenterPixels(fromBox, panelRect) : target;
  piece.knockActive = true;
  piece.knockProgress = 0;
  piece.knockDuration = 0.7;
  piece.knockFrom = pixelToNormalized(from, panelRect);
  piece.knockTo = pixelToNormalized(target, panelRect);
  piece.knockSpin = randomBetween(-Math.PI * 2.5, Math.PI * 2.5);
  piece.knockImpact = false;
  piece.entryActive = false;
  piece.spawnActive = false;
  piece.finishActive = false;
}

export function startFinishForGroup(pieces, panelRect, options = null) {
  if (!pieces || !pieces.length) {
    return;
  }
  const fromServer = options?.fromServer !== false;
  const groupCount = pieces.length;
  const ordered = pieces.slice().sort((a, b) => a.id.localeCompare(b.id));
  const delayMap = new Map();
  ordered.forEach((piece, index) => {
    delayMap.set(piece.id, index * FINISH_TO_GOAL_STAGGER);
  });
  const boardRect = getLogicBoardRect(panelRect);
  for (const piece of pieces) {
    if (piece.finishActive) {
      continue;
    }
    const fromBox = piece.boxId != null ? getBoxById(piece.boxId) : null;
    const from = fromBox ? getBoxCenterPixels(fromBox, panelRect) : { x: panelRect.left, y: panelRect.bottom };
    if (piece.goalIndex == null && !fromServer) {
      piece.goalIndex = gameState.goalCounts[piece.team];
      gameState.goalCounts[piece.team] += 1;
    }
    if (!piece.goalOffset) {
      piece.goalOffset = pickGoalOffset(piece.team, panelRect, boardRect, piece.id);
    }
    const goalCenter = getGoalCenter(piece, panelRect, boardRect);
    piece.finishActive = true;
    piece.finishPhase = "celebrate";
    piece.finishProgress = 0;
    piece.finishJumpCount = 0;
    piece.finishFrom = pixelToNormalized(from, panelRect);
    piece.finishTo = pixelToNormalized(goalCenter, panelRect);
    piece.finishDelayRemaining = delayMap.get(piece.id) || 0;
    piece.entryActive = false;
    piece.spawnActive = false;
    piece.knockActive = false;
    if (!fromServer && piece.team === "player") {
      const origin = pixelToNormalized(from, panelRect);
      const goalScore = getGroupGoalScore(
        scoreConfig.goal,
        scoreConfig.goal2,
        scoreConfig.goal3,
        scoreConfig.goal4,
        groupCount
      );
      const blueGoalScore = getGroupGoalScore(
        scoreConfig.blueGoal,
        scoreConfig.blueGoal2,
        scoreConfig.blueGoal3,
        scoreConfig.blueGoal4,
        groupCount
      );
      queueScoreEvent(goalScore, origin);
      queueScoreEvent(blueGoalScore, origin, {
        targetKey: "blue",
        spriteKey: "blue",
      });
    }
  }
}

function getGroupGoalScore(base, two, three, four, count) {
  const size = Math.max(1, Math.round(count || 1));
  if (size >= 4 && Number.isFinite(four)) {
    return four;
  }
  if (size === 3 && Number.isFinite(three)) {
    return three;
  }
  if (size === 2 && Number.isFinite(two)) {
    return two;
  }
  return base;
}

function pickGoalOffset(team, panelRect, boardRect, excludeId) {
  const baseSize = getBenchBaseSize(panelRect) * ROSTER_SCALE;
  const minDist = baseSize * 0.85;
  const existing = rosterState.tokens.filter(
    (token) =>
      token.team === team &&
      token.goalOffset &&
      token.id !== excludeId &&
      (token.status === "finished" || token.finishActive)
  );
  for (let i = 0; i < 20; i += 1) {
    const candidate = { x: randomBetween(0.1, 0.9), y: randomBetween(0.1, 0.9) };
    const pos = getGoalCenterFromOffset(team, candidate, panelRect, boardRect);
    let ok = true;
    for (const token of existing) {
      const otherPos = getGoalCenterFromOffset(team, token.goalOffset, panelRect, boardRect);
      const dx = pos.x - otherPos.x;
      const dy = pos.y - otherPos.y;
      if (dx * dx + dy * dy < minDist * minDist) {
        ok = false;
        break;
      }
    }
    if (ok) {
      return candidate;
    }
  }
  return { x: randomBetween(0.1, 0.9), y: randomBetween(0.1, 0.9) };
}

export function getLogicBoardRect(panelRect) {
  if (!textures.board) {
    return panelRect;
  }
  const finalBoardRect = computeContainedRect(panelRect, textures.boardSize, 0.9);
  return {
    left: finalBoardRect.left,
    bottom: boardAnim.active ? boardAnim.y : finalBoardRect.bottom,
    width: finalBoardRect.width,
    height: finalBoardRect.height,
  };
}

export function maybeStartPendingRosterSpawn(panelRect) {
  if (!rosterState.spawnPending) {
    return;
  }
  if (!boardAnim.initialized || boardAnim.active) {
    return;
  }
  if (yutButtonAnim.pending || yutButtonAnim.active || yutButtonAnim.progress < 1) {
    return;
  }
  if (rosterState.spawnUseState) {
    beginInitialSpawnFromState(panelRect);
  } else {
    beginInitialSpawn(panelRect);
  }
  requestDraw();
}

export function resetPiecesForStart() {
  const panelRect = getPanelRectForInput();
  const startBox = editor.startBoxId != null ? getBoxById(editor.startBoxId) : null;
  rosterState.spawnPending = true;
  rosterState.spawnUseState = false;
  rosterState.waitingSignature = "";
  for (const token of rosterState.tokens) {
    token.status = "waiting";
    token.boxId = null;
    token.hasLeftStart = false;
    token.spawnActive = false;
    token.spawnProgress = 0;
    token.spawnDuration = ROSTER_SPAWN_MIN_DURATION;
    token.spawnDelayRemaining = 0;
    token.spawnFrom = null;
    token.spawnTo = null;
    token.entryActive = false;
    token.entryProgress = 0;
    token.entryFrom = null;
    token.entryTo = null;
    token.knockActive = false;
    token.knockProgress = 0;
    token.knockFrom = null;
    token.knockTo = null;
    token.knockImpact = false;
    token.finishActive = false;
    token.finishPhase = "celebrate";
    token.finishProgress = 0;
    token.finishJumpCount = 0;
    token.finishFrom = null;
    token.finishTo = null;
    token.finishDelayRemaining = 0;
    token.goalIndex = null;
    token.goalOffset = null;
    token.waitingPos = null;
    token.separationX = 0;
    token.separationTargetX = 0;
  }

  if (!startBox) {
    return;
  }

  for (const token of rosterState.tokens) {
    if (token.id === ACTIVE_CHARACTER_ID || token.id === ACTIVE_MONSTER_ID) {
      token.status = "active";
      token.boxId = editor.startBoxId;
    }
  }

  if (boardAnim.initialized && !boardAnim.active) {
    beginInitialSpawn(panelRect);
  }
}

export function queueInitialSpawnFromState() {
  const panelRect = getPanelRectForInput();
  rosterState.spawnPending = true;
  rosterState.spawnUseState = true;
  rosterState.waitingSignature = "";
  maybeStartPendingRosterSpawn(panelRect);
}

function beginInitialSpawnFromState(panelRect) {
  rosterState.spawnPending = false;
  rosterState.spawnUseState = false;
  const boardRect = getLogicBoardRect(panelRect);
  snapSeparationTargets(panelRect);
  const startBox = editor.startBoxId != null ? getBoxById(editor.startBoxId) : null;
  for (let i = 0; i < rosterState.tokens.length; i += 1) {
    const token = rosterState.tokens[i];
    let target = getPieceCenter(token, panelRect, boardRect);
    if (token.status === "active" && token.boxId == null && startBox) {
      target = getBoxCenterPixels(startBox, panelRect);
    }
    const from = {
      x: target.x,
      y: getOffscreenTopY(panelRect),
    };
    token.spawnActive = true;
    token.spawnProgress = 0;
    token.spawnDuration = randomBetween(ROSTER_SPAWN_MIN_DURATION, ROSTER_SPAWN_MAX_DURATION);
    token.spawnDelayRemaining = i * ROSTER_SPAWN_STAGGER;
    token.spawnFrom = pixelToNormalized(from, panelRect);
    token.spawnTo = pixelToNormalized(target, panelRect);
  }
}

export function beginInitialSpawn(panelRect) {
  const startBox = editor.startBoxId != null ? getBoxById(editor.startBoxId) : null;
  if (!startBox) {
    return;
  }
  rosterState.spawnPending = false;
  const benchCenters = getBenchCenters(panelRect);
  const baseSize = getBenchBaseSize(panelRect);
  const startCenter = getBoxCenterPixels(startBox, panelRect);
  for (let i = 0; i < rosterState.tokens.length; i += 1) {
    const token = rosterState.tokens[i];
    const baseCenter = token.team === "player" ? benchCenters.player : benchCenters.monster;
    const target =
      token.status === "active"
        ? startCenter
        : {
            x: baseCenter.x + token.offset.x * baseSize,
            y: baseCenter.y + token.offset.y * baseSize,
          };
    const from = {
      x: target.x,
      y: getOffscreenTopY(panelRect),
    };
    token.spawnActive = true;
    token.spawnProgress = 0;
    token.spawnDuration = randomBetween(ROSTER_SPAWN_MIN_DURATION, ROSTER_SPAWN_MAX_DURATION);
    token.spawnDelayRemaining = i * ROSTER_SPAWN_STAGGER;
    token.spawnFrom = pixelToNormalized(from, panelRect);
    token.spawnTo = pixelToNormalized(target, panelRect);
  }
}
